import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Users,
  Shield,
  Database,
  Bell,
  Key,
  Save,
  Plus,
  Edit2,
  CheckCircle,
  XCircle,
  Loader2,
  X,
  Mail,
  Send,
  Eye,
  EyeOff,
  Wifi,
  WifiOff,
  AlertCircle,
  CheckCircle2,
  Server,
  List,
  SlidersHorizontal,
  Tags,
  LayoutGrid,
  Sparkles,
  Factory,
  Cpu,
} from 'lucide-react';
import { Button } from '@/components/common/Button';
import { useToast } from '@/hooks/useToast';
import { usersApi } from '@/api/usersApi';
import { emailApi } from '@/api/emailApi';
import { preferencesApi } from '@/api/preferencesApi';
import { usePreferencesStore } from '@/store/preferencesStore';
import { ReferenceDataManager } from '@/components/settings/ReferenceDataManager';
import { ItemTypes } from '@/pages/catalog/ItemTypes';
import { Collections } from '@/pages/catalog/Collections';
import { ProductionMethods } from '@/pages/catalog/ProductionMethods';
import { Machines } from '@/pages/catalog/Machines';
import { Categories } from '@/pages/Categories';
import type { EmailSettings, NotificationPreferences, UpdateEmailSettingsRequest } from '@/api/emailApi';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: 'ADMIN' | 'MANAGER' | 'VIEWER';
  /** API may return `active` or `isActive` depending on backend version */
  active?: boolean;
  isActive?: boolean;
  createdAt?: string;
  lastLogin?: string;
}

interface SystemStatus {
  database: string;
  version: string;
  environment: string;
}

type SettingsTab = 'users' | 'system' | 'notifications' | 'reference' | 'preferences' | 'catalog';
type CatalogSection = 'item-types' | 'design-families' | 'collections' | 'production-methods' | 'machines';

const CATALOG_SECTIONS: { id: CatalogSection; label: string; icon: typeof Tags }[] = [
  { id: 'item-types', label: 'Item Types', icon: Tags },
  { id: 'design-families', label: 'Design Families', icon: LayoutGrid },
  { id: 'collections', label: 'Collections', icon: Sparkles },
  { id: 'production-methods', label: 'Production Methods', icon: Factory },
  { id: 'machines', label: 'Machines', icon: Cpu },
];

function isCatalogSection(value: string | null): value is CatalogSection {
  return CATALOG_SECTIONS.some((section) => section.id === value);
}

// ─── Add User Modal ────────────────────────────────────────────────────────────

interface AddUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function AddUserModal({ onClose, onSuccess }: AddUserModalProps) {
  const { success: showSuccess, error: showError } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'VIEWER' as 'VIEWER' | 'MANAGER' | 'ADMIN',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      showError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await usersApi.create({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        role: form.role,
      });
      showSuccess('User created successfully');
      onSuccess();
      onClose();
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-charcoal-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">Add New User</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                First Name <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <input
                className="input w-full"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
                placeholder="Jane"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                Last Name <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <input
                className="input w-full"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
              Email <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              className="input w-full"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              placeholder="jane@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
              Password <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              className="input w-full"
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              required
              placeholder="••••••••"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
              Confirm Password <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              className="input w-full"
              type="password"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              required
              placeholder="••••••••"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
              Role
            </label>
            <select
              className="input w-full"
              name="role"
              value={form.role}
              onChange={handleChange}
            >
              <option value="VIEWER">Viewer</option>
              <option value="MANAGER">Manager</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" isLoading={submitting}>
              <Plus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit User Modal ───────────────────────────────────────────────────────────

interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}

function EditUserModal({ user, onClose, onSuccess }: EditUserModalProps) {
  const { success: showSuccess, error: showError } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email,
    role: user.role,
    newPassword: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await usersApi.update(user.id, {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        role: form.role,
      });
      if (resetPassword && form.newPassword) {
        await usersApi.resetPassword(user.id, form.newPassword);
      }
      showSuccess('User updated successfully');
      onSuccess();
      onClose();
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-charcoal-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">Edit User</h2>
            <p className="text-sm text-gray-600 dark:text-warm-sand mt-0.5">{user.email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                First Name
              </label>
              <input
                className="input w-full"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder="Jane"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                Last Name
              </label>
              <input
                className="input w-full"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
              Email
            </label>
            <input
              className="input w-full"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="jane@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
              Role
            </label>
            <select
              className="input w-full"
              name="role"
              value={form.role}
              onChange={handleChange}
            >
              <option value="VIEWER">Viewer</option>
              <option value="MANAGER">Manager</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>

          {/* Reset Password toggle */}
          <div className="p-4 bg-gray-100 dark:bg-charcoal-700/40 rounded-lg border border-gray-200 dark:border-charcoal-700">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={resetPassword}
                onChange={(e) => setResetPassword(e.target.checked)}
                className="w-4 h-4 accent-amber-450"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-warm-cream">Reset Password</span>
            </label>
            {resetPassword && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                  New Password <span className="text-red-600 dark:text-red-400">*</span>
                </label>
                <input
                  className="input w-full"
                  type="password"
                  name="newPassword"
                  value={form.newPassword}
                  onChange={handleChange}
                  required={resetPassword}
                  placeholder="••••••••"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" isLoading={submitting}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Settings Page ─────────────────────────────────────────────────────────────

export function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTab>('users');
  const [catalogSection, setCatalogSection] = useState<CatalogSection>('item-types');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const { success: showSuccess, error: showError } = useToast();

  // ── Email Settings State ──────────────────────────────────────────────────
  const [emailSettings, setEmailSettings] = useState<EmailSettings | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testAddress, setTestAddress] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Email form fields
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('Fun 1st Treasures Inventory');
  const [useTls, setUseTls] = useState(true);
  const [useSsl, setUseSsl] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);

  // ── Notification Preferences State ───────────────────────────────────────
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences | null>(null);
  const [additionalRecipients, setAdditionalRecipients] = useState('');

  const {
    productFormMode,
    supplyFormMode,
    sidebarCollapsed,
    setProductFormMode,
    setSupplyFormMode,
    setSidebarCollapsed,
    syncFromServer,
  } = usePreferencesStore();
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchSystemStatus();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'preferences') {
      setActiveTab('preferences');
    } else if (tab === 'catalog') {
      setActiveTab('catalog');
      const section = searchParams.get('section');
      if (isCatalogSection(section)) {
        setCatalogSection(section);
      }
    }
  }, [searchParams]);

  const openTab = (tab: SettingsTab, section?: CatalogSection) => {
    setActiveTab(tab);
    if (tab === 'catalog') {
      const nextSection = section ?? catalogSection;
      setCatalogSection(nextSection);
      setSearchParams({ tab: 'catalog', section: nextSection });
      return;
    }
    if (tab === 'preferences') {
      setSearchParams({ tab: 'preferences' });
      return;
    }
    setSearchParams({});
  };

  const openCatalogSection = (section: CatalogSection) => {
    setCatalogSection(section);
    setSearchParams({ tab: 'catalog', section });
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await usersApi.getAll();
      setUsers(data as unknown as User[]);
    } catch (error) {
      showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const response = await fetch('/api/health');
      if (response.ok) {
        const data = await response.json();
        setSystemStatus(data);
      }
    } catch {
      // Health endpoint might not exist yet
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await usersApi.updateStatus(userId, !currentStatus);
      showSuccess(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch {
      showError('Failed to update user status');
    }
  };

  const handleChangeRole = async (userId: string, newRole: string) => {
    try {
      await usersApi.updateRole(userId, newRole);
      showSuccess('User role updated successfully');
      fetchUsers();
    } catch {
      showError('Failed to update user role');
    }
  };

  /** Normalise `active` / `isActive` field differences between API versions */
  const isUserActive = (user: User) => !!(user.isActive ?? user.active ?? true);

  // ── Email helpers ─────────────────────────────────────────────────────────

  const populateEmailForm = (s: EmailSettings) => {
    setSmtpHost(s.smtpHost || '');
    setSmtpPort(String(s.smtpPort || 587));
    setSmtpUsername(s.smtpUsername || '');
    setSmtpPassword(''); // never pre-fill password
    setFromAddress(s.fromAddress || '');
    setFromName(s.fromName || 'Fun 1st Treasures Inventory');
    setUseTls(s.useTls ?? true);
    setUseSsl(s.useSsl ?? false);
    setEmailEnabled(s.enabled ?? false);
  };

  const fetchEmailSettings = async () => {
    setEmailLoading(true);
    try {
      const data = await emailApi.getSettings();
      setEmailSettings(data);
      populateEmailForm(data);
    } catch {
      // Not an admin or endpoint not yet available — silent
    } finally {
      setEmailLoading(false);
    }
  };

  const fetchNotifPrefs = async () => {
    try {
      const data = await emailApi.getNotificationPreferences();
      setNotifPrefs(data);
      setAdditionalRecipients(data.additionalRecipients || '');
    } catch {
      // silent
    }
  };

  // Load email settings when notifications tab is opened
  useEffect(() => {
    if (activeTab === 'notifications') {
      fetchEmailSettings();
      fetchNotifPrefs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchPreferences = async () => {
    setPrefsLoading(true);
    try {
      const data = await preferencesApi.getMine();
      syncFromServer(data);
    } catch {
      showError('Failed to load preferences');
    } finally {
      setPrefsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'preferences') {
      fetchPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSavePreferences = async () => {
    setPrefsSaving(true);
    try {
      const updated = await preferencesApi.updateMine({
        productFormMode,
        supplyFormMode,
        sidebarCollapsed,
      });
      syncFromServer(updated);
      showSuccess('Preferences saved successfully');
    } catch {
      showError('Failed to save preferences');
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setEmailSaving(true);
    try {
      const payload: UpdateEmailSettingsRequest = {
        smtpHost,
        smtpPort: parseInt(smtpPort) || 587,
        smtpUsername,
        smtpPassword: smtpPassword.trim() || null,
        fromAddress,
        fromName,
        useTls,
        useSsl,
        enabled: emailEnabled,
      };
      const updated = await emailApi.updateSettings(payload);
      setEmailSettings(updated);
      setSmtpPassword('');
      showSuccess('Email settings saved successfully');
    } catch {
      showError('Failed to save email settings');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testAddress.trim()) { showError('Please enter a test email address'); return; }
    setEmailTesting(true);
    setTestResult(null);
    try {
      const result = await emailApi.testConnection(testAddress.trim());
      setTestResult(result);
      if (result.success) showSuccess('Test email sent successfully!');
      else showError('Test failed: ' + result.message);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Could not send test email';
      setTestResult({ success: false, message: msg });
      showError(msg);
    } finally {
      setEmailTesting(false);
    }
  };

  const handleToggleNotifPref = async (field: keyof NotificationPreferences, value: boolean) => {
    if (!notifPrefs) return;
    const updated = { ...notifPrefs, [field]: value };
    setNotifPrefs(updated);
    try {
      await emailApi.updateNotificationPreferences({ [field]: value });
    } catch {
      setNotifPrefs(notifPrefs); // revert on error
      showError('Failed to update preference');
    }
  };

  const handleSaveAdditionalRecipients = async () => {
    try {
      await emailApi.updateNotificationPreferences({ additionalRecipients });
      showSuccess('Recipients saved');
    } catch {
      showError('Failed to save recipients');
    }
  };

  // Port presets
  const applyPortPreset = (preset: 'starttls' | 'ssl') => {
    if (preset === 'starttls') { setSmtpPort('587'); setUseTls(true); setUseSsl(false); }
    else { setSmtpPort('465'); setUseTls(false); setUseSsl(true); }
  };

  const renderUsersTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">User Management</h3>
          <p className="text-sm text-gray-600 dark:text-warm-sand mt-1">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowAddUser(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-450" />
        </div>
      ) : (
        <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-100 dark:bg-charcoal-700/50">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  User
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Role
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Joined
                </th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700/30">
                  <td className="py-4 px-4">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-warm-cream">{user.fullName}</div>
                      <div className="text-sm text-gray-600 dark:text-warm-sand">{user.email}</div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <select
                      value={user.role}
                      onChange={(e) => handleChangeRole(user.id, e.target.value)}
                      className="bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-lg px-3 py-1.5 text-sm text-gray-900 dark:text-warm-cream focus:ring-2 focus:ring-amber-450/50 focus:border-amber-450"
                    >
                      <option value="VIEWER">Viewer</option>
                      <option value="MANAGER">Manager</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                      isUserActive(user)
                        ? 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20'
                        : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
                    }`}>
                      {isUserActive(user) ? (
                        <><CheckCircle className="w-3.5 h-3.5" /> Active</>
                      ) : (
                        <><XCircle className="w-3.5 h-3.5" /> Inactive</>
                      )}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-600 dark:text-warm-sand">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-4 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:text-warm-cream"
                        onClick={() => setEditingUser(user)}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleUserStatus(user.id, isUserActive(user))}
                        className={isUserActive(user) ? 'text-red-600 dark:text-red-400 hover:text-red-300' : 'text-green-600 dark:text-green-400 hover:text-green-300'}
                      >
                        {isUserActive(user)
                          ? <><XCircle className="w-4 h-4 mr-1" /> Deactivate</>
                          : <><CheckCircle className="w-4 h-4 mr-1" /> Activate</>
                        }
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSystemTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">System Status</h3>
        <p className="text-sm text-gray-600 dark:text-warm-sand mt-1">
          View system health and configuration
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-green-500/10">
              <Database className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Database</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-warm-cream">
                {systemStatus?.database || 'Connected'}
              </p>
            </div>
          </div>
          <div className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5" />
            Operational
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Version</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-warm-cream">3.5.29</p>
            </div>
          </div>
          <div className="text-xs text-gray-600 dark:text-warm-sand">
            Enterprise Edition
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-purple-500/10">
              <Key className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Environment</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-warm-cream">
                {systemStatus?.environment || 'Production'}
              </p>
            </div>
          </div>
          <div className="text-xs text-gray-600 dark:text-warm-sand">
            Secure Connection
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 p-6">
        <h4 className="font-medium text-gray-900 dark:text-warm-cream mb-4">System Features</h4>
        <div className="space-y-3">
          {[
            { name: 'Production Methods', status: 'Active', desc: '3D Print, UV, Laser, DTF, Vinyl, and more' },
            { name: 'Product Types (Catalog)', status: 'Active', desc: 'Keychain, Sign, Apparel, Trophy, etc.' },
            { name: 'Multi-Filament Support', status: 'Active', desc: 'Up to 8 colors per 3D printed product' },
            { name: 'Supply Inventory (BOM)', status: 'Active', desc: 'Track components and costs' },
            { name: 'Audit Logging', status: 'Active', desc: 'Full change tracking' },
            { name: 'Location-based Sales', status: 'Active', desc: 'Restrict sales by location' },
          ].map((feature) => (
            <div key={feature.name} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-charcoal-700 last:border-0">
              <div>
                <p className="font-medium text-gray-900 dark:text-warm-cream">{feature.name}</p>
                <p className="text-sm text-gray-600 dark:text-warm-sand">{feature.desc}</p>
              </div>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20">
                {feature.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
      <input type="checkbox" className="sr-only peer" checked={checked} onChange={e => onChange(e.target.checked)} />
      <div className="w-11 h-6 bg-gray-100 dark:bg-charcoal-700 peer-focus:outline-none rounded-full peer
        peer-checked:after:translate-x-full peer-checked:after:border-white
        after:content-[''] after:absolute after:top-[2px] after:left-[2px]
        after:bg-white after:border-gray-300 after:border after:rounded-full
        after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-450" />
    </label>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-8">

      {/* ── SMTP / Email Server Section ─────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <Server className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">Email Server (SMTP)</h3>
            <p className="text-sm text-gray-600 dark:text-warm-sand">Configure your MXroute or standard SMTP server</p>
          </div>
          {/* Connection status pill */}
          <div className="ml-auto">
            {emailSettings?.enabled ? (
              emailSettings.lastTestSuccess === true ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20">
                  <Wifi className="w-3.5 h-3.5" /> Connected
                </span>
              ) : emailSettings.lastTestSuccess === false ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
                  <WifiOff className="w-3.5 h-3.5" /> Connection Failed
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                  <Mail className="w-3.5 h-3.5" /> Enabled — Untested
                </span>
              )
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-charcoal-700 text-gray-600 dark:text-warm-sand border border-gray-300 dark:border-charcoal-600">
                <WifiOff className="w-3.5 h-3.5" /> Disabled
              </span>
            )}
          </div>
        </div>

        {emailLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-7 h-7 animate-spin text-amber-450" />
          </div>
        ) : (
          <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 p-6 space-y-5">

            {/* Enable toggle */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-charcoal-700">
              <div>
                <p className="font-medium text-gray-900 dark:text-warm-cream">Enable Email Notifications</p>
                <p className="text-sm text-gray-600 dark:text-warm-sand">Turn on to start sending system email alerts</p>
              </div>
              <Toggle checked={emailEnabled} onChange={setEmailEnabled} />
            </div>

            {/* MXroute quick-help banner */}
            <div className="flex items-start gap-3 p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
              <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-300 mb-1">MXroute Setup Guide</p>
                <p className="text-gray-600 dark:text-warm-sand/80 leading-relaxed">
                  Use <span className="font-mono text-gray-900 dark:text-warm-cream">mail.mxroute.com</span> (or your assigned server from the MXroute welcome email) as the host.
                  Port <span className="font-mono text-gray-900 dark:text-warm-cream">587</span> with STARTTLS is recommended.
                  Your username is your <strong className="text-gray-900 dark:text-warm-cream">full email address</strong> (e.g. <span className="font-mono">inventory@fun1sttreasures.com</span>).
                </p>
              </div>
            </div>

            {/* SMTP Host + Port */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-2">SMTP Host</label>
                <input
                  type="text"
                  value={smtpHost}
                  onChange={e => setSmtpHost(e.target.value)}
                  placeholder="mail.mxroute.com"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-2">Port</label>
                <input
                  type="number"
                  value={smtpPort}
                  onChange={e => setSmtpPort(e.target.value)}
                  placeholder="587"
                  className="input"
                />
              </div>
            </div>

            {/* Port preset buttons */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-600 dark:text-warm-sand">Quick presets:</span>
              <button
                type="button"
                onClick={() => applyPortPreset('starttls')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                  useTls && !useSsl && smtpPort === '587'
                    ? 'bg-amber-450/10 border-amber-450/40 text-amber-600 dark:text-amber-400'
                    : 'border-gray-300 dark:border-charcoal-600 text-gray-600 dark:text-warm-sand hover:border-charcoal-500 hover:text-gray-900 dark:text-warm-cream'
                }`}
              >
                Port 587 — STARTTLS <span className="text-gray-600 dark:text-warm-sand/60">(recommended)</span>
              </button>
              <button
                type="button"
                onClick={() => applyPortPreset('ssl')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                  useSsl && !useTls && smtpPort === '465'
                    ? 'bg-amber-450/10 border-amber-450/40 text-amber-600 dark:text-amber-400'
                    : 'border-gray-300 dark:border-charcoal-600 text-gray-600 dark:text-warm-sand hover:border-charcoal-500 hover:text-gray-900 dark:text-warm-cream'
                }`}
              >
                Port 465 — SSL/TLS
              </button>
            </div>

            {/* TLS / SSL toggles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-gray-100 dark:bg-charcoal-700/40 rounded-xl border border-gray-200 dark:border-charcoal-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-warm-cream">STARTTLS</p>
                  <p className="text-xs text-gray-600 dark:text-warm-sand">Upgrade connection to TLS (port 587)</p>
                </div>
                <Toggle checked={useTls} onChange={v => { setUseTls(v); if (v) setUseSsl(false); }} />
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-100 dark:bg-charcoal-700/40 rounded-xl border border-gray-200 dark:border-charcoal-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-warm-cream">SSL/TLS</p>
                  <p className="text-xs text-gray-600 dark:text-warm-sand">Encrypted from the start (port 465)</p>
                </div>
                <Toggle checked={useSsl} onChange={v => { setUseSsl(v); if (v) setUseTls(false); }} />
              </div>
            </div>

            {/* Username + Password */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-2">Username (Email Address)</label>
                <input
                  type="email"
                  value={smtpUsername}
                  onChange={e => setSmtpUsername(e.target.value)}
                  placeholder="inventory@fun1sttreasures.com"
                  className="input"
                />
                <p className="mt-1.5 text-xs text-gray-600 dark:text-warm-sand/60">MXroute uses your full email address as the username</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-2">
                  Password
                  {emailSettings?.smtpPasswordSet && (
                    <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-normal">● Password saved</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={smtpPassword}
                    onChange={e => setSmtpPassword(e.target.value)}
                    placeholder={emailSettings?.smtpPasswordSet ? '••••••••  (leave blank to keep)' : 'Enter password'}
                    className="input pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:text-warm-cream transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-gray-600 dark:text-warm-sand/60">Leave blank to keep the existing password</p>
              </div>
            </div>

            {/* From address + name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-2">From Address</label>
                <input
                  type="email"
                  value={fromAddress}
                  onChange={e => setFromAddress(e.target.value)}
                  placeholder="inventory@fun1sttreasures.com"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-2">From Name</label>
                <input
                  type="text"
                  value={fromName}
                  onChange={e => setFromName(e.target.value)}
                  placeholder="Fun 1st Treasures Inventory"
                  className="input"
                />
              </div>
            </div>

            {/* Last test result */}
            {emailSettings?.lastTestAt && (
              <div className={`flex items-start gap-3 p-4 rounded-xl border ${
                emailSettings.lastTestSuccess
                  ? 'bg-green-500/5 border-green-500/20'
                  : 'bg-red-500/5 border-red-500/20'
              }`}>
                {emailSettings.lastTestSuccess
                  ? <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  : <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                }
                <div className="text-sm">
                  <p className={`font-medium ${emailSettings.lastTestSuccess ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    Last test {emailSettings.lastTestSuccess ? 'passed' : 'failed'}
                    <span className="font-normal text-gray-600 dark:text-warm-sand ml-2">
                      {new Date(emailSettings.lastTestAt).toLocaleString()}
                    </span>
                  </p>
                  {emailSettings.lastTestError && (
                    <p className="text-red-300/80 mt-1 font-mono text-xs">{emailSettings.lastTestError}</p>
                  )}
                </div>
              </div>
            )}

            {/* Test result (live) */}
            {testResult && (
              <div className={`flex items-start gap-3 p-4 rounded-xl border ${
                testResult.success ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'
              }`}>
                {testResult.success
                  ? <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  : <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                }
                <p className={`text-sm ${testResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {testResult.message}
                </p>
              </div>
            )}

            {/* Action row */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2 border-t border-gray-200 dark:border-charcoal-700">
              {/* Send test email */}
              <div className="flex items-center gap-2 flex-1 w-full">
                <input
                  type="email"
                  value={testAddress}
                  onChange={e => setTestAddress(e.target.value)}
                  placeholder="Send test to this address..."
                  className="input flex-1 py-2 text-sm"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleTestEmail}
                  isLoading={emailTesting}
                  disabled={emailTesting || !smtpHost}
                  type="button"
                >
                  <Send className="w-4 h-4 mr-1.5" />
                  Test
                </Button>
              </div>
              <Button
                variant="primary"
                onClick={handleSaveEmailSettings}
                isLoading={emailSaving}
                type="button"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Notification Preferences Section ───────────────────────────── */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-amber-450/10">
            <Bell className="w-5 h-5 text-amber-450" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">My Notification Preferences</h3>
            <p className="text-sm text-gray-600 dark:text-warm-sand">Choose which events trigger email alerts to you</p>
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 divide-y divide-charcoal-700">
          {[
            { field: 'notifyLowStock' as const,          label: 'Product Low Stock',       desc: 'Alert when shelf inventory falls below threshold' },
            { field: 'notifySupplyLowStock' as const,    label: 'Supply Low Stock',        desc: 'Alert when supply inventory drops to reorder level' },
            { field: 'notifyTransfer' as const,          label: 'Inventory Transfers',     desc: 'Notify on incoming or outgoing stock transfers' },
            { field: 'notifyNewSale' as const,           label: 'New Sale Recorded',       desc: 'Email when a sale is logged in the system' },
            { field: 'notifyDailySalesSummary' as const, label: 'Daily Sales Summary',     desc: 'Receive a summary of the day\'s sales each evening' },
            { field: 'notifyUserCreated' as const,       label: 'New User Created',        desc: 'Notify when a new user account is added' },
          ].map(({ field, label, desc }) => (
            <div key={field} className="flex items-center justify-between px-5 py-4">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-warm-cream">{label}</p>
                <p className="text-xs text-gray-600 dark:text-warm-sand mt-0.5">{desc}</p>
              </div>
              <Toggle
                checked={notifPrefs?.[field] ?? false}
                onChange={v => handleToggleNotifPref(field, v)}
              />
            </div>
          ))}
        </div>

        {/* Additional recipients */}
        <div className="mt-5 bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 p-5">
          <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-1">
            Additional Email Recipients
          </label>
          <p className="text-xs text-gray-600 dark:text-warm-sand mb-3">
            Comma-separated extra addresses that also receive notifications (e.g. a shared inbox).
          </p>
          <div className="flex gap-3">
            <input
              type="text"
              value={additionalRecipients}
              onChange={e => setAdditionalRecipients(e.target.value)}
              placeholder="billing@fun1sttreasures.com, owner@gmail.com"
              className="input flex-1 text-sm"
            />
            <Button variant="secondary" size="sm" type="button" onClick={handleSaveAdditionalRecipients}>
              <Save className="w-4 h-4 mr-1.5" />
              Save
            </Button>
          </div>
        </div>
      </div>

    </div>
  );

  const renderPreferencesTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">My Preferences</h3>
        <p className="text-sm text-gray-600 dark:text-warm-sand mt-1">
          Customize how the app looks and how you add products and supplies
        </p>
      </div>

      {prefsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-450" />
        </div>
      ) : (
        <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 divide-y divide-charcoal-700">
          <div className="px-5 py-4">
            <p className="text-sm font-medium text-gray-900 dark:text-warm-cream mb-3">Product Form Mode</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setProductFormMode('wizard')}
                className={`p-4 rounded-xl border text-left transition-all ${
                  productFormMode === 'wizard'
                    ? 'bg-amber-450/10 border-amber-450/40'
                    : 'border-gray-300 dark:border-charcoal-600 hover:border-charcoal-500'
                }`}
              >
                <p className="font-medium text-gray-900 dark:text-warm-cream">Wizard</p>
                <p className="text-xs text-gray-600 dark:text-warm-sand mt-1">
                  Step-by-step guided product creation
                </p>
              </button>
              <button
                type="button"
                onClick={() => setProductFormMode('single')}
                className={`p-4 rounded-xl border text-left transition-all ${
                  productFormMode === 'single'
                    ? 'bg-amber-450/10 border-amber-450/40'
                    : 'border-gray-300 dark:border-charcoal-600 hover:border-charcoal-500'
                }`}
              >
                <p className="font-medium text-gray-900 dark:text-warm-cream">Single Page</p>
                <p className="text-xs text-gray-600 dark:text-warm-sand mt-1">
                  All fields on one scrollable form
                </p>
              </button>
            </div>
          </div>

          <div className="px-5 py-4">
            <p className="text-sm font-medium text-gray-900 dark:text-warm-cream mb-3">Supply Form Mode</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSupplyFormMode('wizard')}
                className={`p-4 rounded-xl border text-left transition-all ${
                  supplyFormMode === 'wizard'
                    ? 'bg-amber-450/10 border-amber-450/40'
                    : 'border-gray-300 dark:border-charcoal-600 hover:border-charcoal-500'
                }`}
              >
                <p className="font-medium text-gray-900 dark:text-warm-cream">Wizard</p>
                <p className="text-xs text-gray-600 dark:text-warm-sand mt-1">
                  Step-by-step guided supply creation
                </p>
              </button>
              <button
                type="button"
                onClick={() => setSupplyFormMode('single')}
                className={`p-4 rounded-xl border text-left transition-all ${
                  supplyFormMode === 'single'
                    ? 'bg-amber-450/10 border-amber-450/40'
                    : 'border-gray-300 dark:border-charcoal-600 hover:border-charcoal-500'
                }`}
              >
                <p className="font-medium text-gray-900 dark:text-warm-cream">Single Page</p>
                <p className="text-xs text-gray-600 dark:text-warm-sand mt-1">
                  All supply fields on one scrollable form
                </p>
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-warm-cream">Collapse Sidebar</p>
              <p className="text-xs text-gray-600 dark:text-warm-sand mt-0.5">
                Show a compact icon-only sidebar on desktop
              </p>
            </div>
            <Toggle checked={sidebarCollapsed} onChange={setSidebarCollapsed} />
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSavePreferences}
          isLoading={prefsSaving}
          disabled={prefsLoading}
          type="button"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Preferences
        </Button>
      </div>
    </div>
  );

  const renderCatalogTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">Product Catalog</h2>
        <p className="text-sm text-gray-600 dark:text-warm-sand mt-1">
          Manage item types, design families, collections, and production methods
        </p>
      </div>

      <div className="border-b border-gray-200 dark:border-charcoal-700">
        <nav className="flex flex-wrap gap-2 pb-4">
          {CATALOG_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => openCatalogSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                catalogSection === section.id
                  ? 'bg-amber-450/10 text-amber-450 border border-amber-450/20'
                  : 'text-gray-600 dark:text-warm-sand hover:bg-gray-100 dark:hover:bg-charcoal-700'
              }`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
            </button>
          ))}
        </nav>
      </div>

      {catalogSection === 'item-types' && <ItemTypes embedded />}
      {catalogSection === 'design-families' && <Categories embedded />}
      {catalogSection === 'collections' && <Collections embedded />}
      {catalogSection === 'production-methods' && <ProductionMethods embedded />}
      {catalogSection === 'machines' && <Machines embedded />}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl bg-amber-450/10 border border-amber-450/20">
          <SettingsIcon className="w-6 h-6 text-amber-450" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-warm-cream">Settings</h1>
          <p className="text-gray-600 dark:text-warm-sand">Manage system configuration and user accounts</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-charcoal-700">
        <nav className="flex gap-6">
          {[
            { id: 'users', label: 'Users', icon: Users },
            { id: 'catalog', label: 'Catalog', icon: LayoutGrid },
            { id: 'reference', label: 'Reference Data', icon: List },
            { id: 'preferences', label: 'Preferences', icon: SlidersHorizontal },
            { id: 'system', label: 'System', icon: Database },
            { id: 'notifications', label: 'Email & Alerts', icon: Mail },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => openTab(tab.id as SettingsTab, tab.id === 'catalog' ? catalogSection : undefined)}
              className={`flex items-center gap-2 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-amber-450 text-amber-450'
                  : 'border-transparent text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:text-warm-cream'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="py-4">
        {activeTab === 'users' && renderUsersTab()}
        {activeTab === 'catalog' && renderCatalogTab()}
        {activeTab === 'reference' && (
          <div className="space-y-8">
            <ReferenceDataManager type="suppliers" />
            <ReferenceDataManager type="colors" />
            <ReferenceDataManager type="brands" />
          </div>
        )}
        {activeTab === 'system' && renderSystemTab()}
        {activeTab === 'notifications' && renderNotificationsTab()}
        {activeTab === 'preferences' && renderPreferencesTab()}
      </div>

      {/* Modals */}
      {showAddUser && (
        <AddUserModal
          onClose={() => setShowAddUser(false)}
          onSuccess={fetchUsers}
        />
      )}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={fetchUsers}
        />
      )}
    </div>
  );
}
